/**
 * Format history events to human-readable English strings.
 * Single source of truth for event text formatting in the dashboard.
 */

import { HistoryEvent, HistoryAction } from '@/types/history';

const actionMessages: Record<HistoryAction, (details?: Record<string, any>) => string> = {
  // User actions
  capturing_started: () => 'Shooting started',
  capturing_stopped: () => 'Shooting stopped',
  interval_changed: (d) =>
    d ? `Interval changed from ${d.from}s to ${d.to}s` : 'Interval changed',
  schedule_changed: () => 'Schedule changed',
  camera_settings_changed: (d) =>
    d ? `Camera setting changed: ${d.field} (${d.from} → ${d.to})` : 'Camera settings changed',
  raw_toggled: (d) =>
    d?.enabled ? 'RAW capture enabled' : 'RAW capture disabled',
  test_shot_requested: () => 'Test shot requested',

  // State changes
  test_shot_completed: (d) => {
    if (d?.camera) return `Test shot completed (${d.camera}, ISO ${d.iso}, ${d.shutterSpeed})`;
    return 'Test shot completed';
  },
  test_shot_failed: (d) =>
    d?.message ? `Test shot failed: ${d.message}` : 'Test shot failed',
  view_renamed: (d) =>
    d ? `View renamed: "${d.from}" → "${d.to}"` : 'View renamed',
  camera_connected: (d) =>
    d?.model ? `Camera connected: ${d.model}` : 'Camera connected',
  camera_disconnected: () => 'Camera disconnected',
  device_online: () => 'Device online',
  device_offline: () => 'Device offline',
  battery_low: (d) =>
    d?.level != null ? `Battery low: ${d.level}%` : 'Battery low',
  battery_restored: (d) =>
    d?.level != null ? `Battery restored: ${d.level}%` : 'Battery restored',
  battery_temperature_warning: (d) =>
    d?.temperature != null ? `Battery temperature warning: ${d.temperature}°C` : 'Battery temperature warning',
  battery_temperature_critical: (d) =>
    d?.temperature != null ? `Battery temperature critical: ${d.temperature}°C` : 'Battery temperature critical',
  battery_temperature_resolved: (d) =>
    d?.temperature != null ? `Battery temperature normal: ${d.temperature}°C` : 'Battery temperature normal',
  charger_connected: () => 'Charger connected',
  charger_disconnected: () => 'Charger disconnected',
  app_updated: (d) =>
    d?.fromVersion && d?.toVersion
      ? `App updated\n${d.fromVersion} → ${d.toVersion}`
      : 'App updated',

  // MasterPower
  master_power_connected: () => 'MasterPower connected',
  master_power_disconnected: () => 'MasterPower disconnected',
  master_power_cycle: (d) => {
    const ch = d?.channel === 'both' ? 'camera + hub' : d?.channel || 'unknown';
    const reason = d?.reason === 'auto_recovery' ? 'auto' : 'manual';
    return `Power cycled ${ch} (${reason})`;
  },

  // Cloud Sync (from export-worker)
  cloud_sync_paused: (d) => {
    const reasons: Record<string, string> = {
      quota_full: 'cloud storage full',
      access_revoked: 'account access revoked',
      provider_error: 'provider error',
    };
    const reason = d?.reason ? reasons[d.reason] ?? d.reason : null;
    return reason ? `Cloud sync paused (${reason})` : 'Cloud sync paused';
  },
  cloud_sync_resumed: (d) => {
    const reasons: Record<string, string> = {
      space_available: 'space available again',
      account_reconnected: 'account reconnected',
      user: 'resumed by you',
    };
    const reason = d?.reason ? reasons[d.reason] ?? d.reason : null;
    return reason ? `Cloud sync resumed (${reason})` : 'Cloud sync resumed';
  },
  cloud_sync_photo_failed: (d) =>
    d?.photoId ? `Cloud sync: photo upload failed (${d.photoId})` : 'Cloud sync: photo upload failed',

  // Errors (from Android)
  capture_failed: (d) =>
    d?.message ? `Capture failed: ${d.message}` : 'Capture failed',
  upload_failed: (d) =>
    d?.retries != null ? `Upload failed after ${d.retries} retries` : 'Upload failed',
  camera_error: (d) =>
    d?.message ? `Camera error: ${d.message}` : 'Camera error',
};

export function formatHistoryEvent(event: HistoryEvent): string {
  const formatter = actionMessages[event.action];
  if (formatter) {
    return formatter(event.details);
  }
  return event.action;
}
